import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import {
  getDocumentsTreeNodes,
  getPropertyDocumentDownload,
  getDocumentsPropertyTreeCurrent,
} from "../api/documentApi";

import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ApartmentIcon from "@mui/icons-material/Apartment";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { getAccessibleProperties } from "../api/propertyApi";

function DocumentsPage() {
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState({});
  const [childrenMap, setChildrenMap] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  const [error, setError] = useState("");
  const [properties, setProperties] = useState([]);
  const [liveFilter, setLiveFilter] = useState("live");

  useEffect(() => {
    setCustomPath([
      { label: "Documents", path: null },
    ]);
    return () => clearCustomPath();
  }, [setCustomPath, clearCustomPath]);

  const loadChildren = async (nodeId) => {
    setLoadingNodes((prev) => new Set(prev).add(nodeId));
    setError("");
    try {
      const data = await getDocumentsTreeNodes(nodeId);
      setNodes((prev) => {
        const next = { ...prev };
        data.forEach((node) => {
          next[node.id] = node;
        });
        return next;
      });
      setChildrenMap((prev) => ({
        ...prev,
        [nodeId]: data.map((node) => node.id),
      }));
      return data;
    } catch (err) {
      console.error(err);
      setError("Failed to load documents tree.");
    } finally {
      setLoadingNodes((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    }
    return [];
  };

  useEffect(() => {
    loadChildren("root");
  }, []);

  useEffect(() => {
    const loadProperties = async () => {
      try {
        const apiLiveFilter =
          liveFilter === "all" ? null : liveFilter === "live";
        const data = await getAccessibleProperties(apiLiveFilter);
        const uniqueProps = [
          ...new Map((data || []).map((property) => [property.property_id, property])).values(),
        ];
        setProperties(uniqueProps);
      } catch (err) {
        console.error(err);
        setError("Failed to load documents tree.");
      }
    };

    loadProperties();
  }, [liveFilter]);

  const isExpanded = (nodeId) => expanded.has(nodeId);
  const isLoading = (nodeId) => loadingNodes.has(nodeId);
  const childrenFor = (nodeId) => childrenMap[nodeId] || [];

  const prefetchPropertyNodes = async (propertyNodeId) => {
    const propertyId = propertyNodeId.split(":")[1];
    if (!propertyId) return;
    setLoadingNodes((prev) => new Set(prev).add(propertyNodeId));
    setError("");
    try {
      const data = await getDocumentsPropertyTreeCurrent(propertyId);
      const returnedNodes = data?.nodes || [];
      const returnedChildren = data?.children || {};
      setNodes((prev) => {
        const next = { ...prev };
        returnedNodes.forEach((node) => {
          next[node.id] = node;
        });
        return next;
      });
      setChildrenMap((prev) => ({
        ...prev,
        ...returnedChildren,
      }));
    } catch (err) {
      console.error(err);
      setError("Failed to load documents tree.");
    } finally {
      setLoadingNodes((prev) => {
        const next = new Set(prev);
        next.delete(propertyNodeId);
        return next;
      });
    }
  };

  const toggleNode = (nodeId, hasChildren, node) => {
    if (!hasChildren) return;
    const wasExpanded = expanded.has(nodeId);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
    if (node?.type === "property" && !wasExpanded) {
      prefetchPropertyNodes(nodeId);
      return;
    }
    if (!childrenMap[nodeId]) {
      loadChildren(nodeId);
    }
  };

  const rootChildren = useMemo(() => {
    const allowedPropertyIds = new Set(properties.map((property) => property.property_id));
    return (childrenMap.root || []).filter((nodeId) => {
      const propertyId = Number(nodeId.split(":")[1]);
      return allowedPropertyIds.has(propertyId);
    });
  }, [childrenMap, properties]);

  const iconFor = (node) => {
    if (node.type === "property") return <ApartmentIcon />;
    if (node.type === "group") return <FolderIcon />;
    if (node.type === "document") {
      const label = String(node.label || "");
      const ext = label.split(".").pop()?.toLowerCase?.() || "";
      if (ext === "pdf") return <PictureAsPdfIcon />;
      if (["png", "jpg", "jpeg", "gif", "bmp", "svg", "webp"].includes(ext)) {
        return <ImageIcon />;
      }
      if (["xls", "xlsx", "csv"].includes(ext)) return <TableChartIcon />;
      if (["doc", "docx", "rtf", "txt"].includes(ext)) {
        return <DescriptionIcon />;
      }
      return <InsertDriveFileIcon />;
    }
    return <DescriptionIcon />;
  };

  const handleDownload = async (node) => {
    if (!node?.property_id || !node?.document_id) return;
    try {
      const response = await getPropertyDocumentDownload(
        node.property_id,
        node.document_id
      );
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = match ? decodeURIComponent(match[1]) : node.label || "document";
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError("Failed to download document.");
    }
  };

  const renderNodes = (nodeIds, depth = 0) => (
    <List component="div" disablePadding>
      {nodeIds.map((nodeId) => {
        const node = nodes[nodeId];
        if (!node) return null;
        const open = isExpanded(nodeId);
        const hasChildren = Boolean(node.has_children);
        return (
          <React.Fragment key={nodeId}>
            <ListItemButton
              onClick={() => {
                if (node.type === "document") {
                  handleDownload(node);
                } else {
                  toggleNode(nodeId, hasChildren, node);
                }
              }}
              sx={{ pl: 2 + depth * 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {iconFor(node)}
              </ListItemIcon>
              <ListItemText primary={node.label} />
              {node.type === "property" && (
                <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      const propertyId = node.id.split(":")[1];
                      navigate(`/properties/${propertyId}/insurance`);
                    }}
                  >
                    Insurance
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      const propertyId = node.id.split(":")[1];
                      navigate(`/properties/${propertyId}/utilities`);
                    }}
                  >
                    Utilities
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      const propertyId = node.id.split(":")[1];
                      navigate(`/properties/${propertyId}/assessments`);
                    }}
                  >
                    Assessments
                  </Button>
                </Box>
              )}
              {hasChildren && (
                isLoading(nodeId) ? (
                  <CircularProgress size={18} />
                ) : open ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )
              )}
            </ListItemButton>
            {hasChildren && (
              <Collapse in={open} timeout="auto" unmountOnExit>
                {renderNodes(childrenFor(nodeId), depth + 1)}
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );

  return (
    <Layout>
      <Box className="page-container">
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Documents
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={liveFilter}
              label="Filter"
              onChange={(event) => setLiveFilter(event.target.value)}
            >
              <MenuItem value="all">All Properties</MenuItem>
              <MenuItem value="live">Live Only</MenuItem>
              <MenuItem value="non-live">Non-Live Only</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={2} sx={{ p: 2 }}>
          {isLoading("root") ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading documents tree...</Typography>
            </Box>
          ) : rootChildren.length ? (
            renderNodes(rootChildren)
          ) : (
            <Typography variant="body2" color="text.secondary">
              No documents found.
            </Typography>
          )}
        </Paper>
      </Box>
    </Layout>
  );
}

export default DocumentsPage;
